import {
  CheckCircleFilled,
  CloseOutlined,
  SlackOutlined,
  SyncOutlined,
  WarningFilled,
} from '@ant-design/icons';
import {
  BackButton,
  Button,
  Heading,
  Input,
  Line,
  Tooltip,
} from '@mm/components';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import {
  CampaignType,
  SoldConfigurationsType,
} from '@mm/state/model/CampaignsModel';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useFormik } from 'formik';
import { cloneDeep, isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import css from './AutomationSoLDSlackNotificationSettings.module.css';
import { Editor } from '../SlideoutEmail/editor/components/Editor';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as SlackIcon } from 'assets/slack.svg';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { SlackAccount } from '@mm/state/model/SlackModel';
import {
  selectSlackAccounts,
  selectSlackChannels,
} from '@mm/state/selectors/slackSelectors';
import { useSlackCRUDActions } from '@mm/state/actions/SlackCRUDActions';
import ImageUploader from '../../../../../common/components/ImageUploader/ImageUploader';
import { useConnectSlack } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/SlackIntegrations/use-connect-slack';

import SOLDSlackHelper, { SlackFormFields } from './schema-helper';
import classNames from 'classnames';
import {
  getHTMLStringFromState,
  getHTMLText,
} from '../SlideoutEmail/editor/utils';
import { soldConfigInitialValues } from '../AutomationSoLDSettingsSlideout/AutomationSoLDSettingsSlideout';
import { SlideOutMenuType } from '@mm/state/interfaces/view';

export type IAutomationSoLDSlackNotificationSettings = {
  menuType: SlideOutMenuType;
  campaignId?: number;
  onClose?: () => void;
};

export const AutomationSoLDSlackNotificationSettings: React.FC<
  IAutomationSoLDSlackNotificationSettings
> = props => {
  const { menuType, campaignId = -1, onClose: onCloseProp = () => {} } = props;

  const chatbotId = useSelector(activeChatbotIdSelector);
  const campaign = useSelector(state => selectCampaignById(state, campaignId));
  const slackBusy = useSelector((state: StoreState) => state.slack.busy);
  const attributes = useSelector(
    (state: StoreState) => state.attribute.entities,
  );
  const accounts = useSelector(selectSlackAccounts);
  const slackChannels = useSelector(selectSlackChannels);

  const initials = cloneDeep(
    campaign?.soldConfigurations || soldConfigInitialValues,
  ) as SoldConfigurationsType;
  if (!campaign?.soldConfigurations?.unicorn.notifications.slack) {
    initials.unicorn.notifications.slack = {
      ...soldConfigInitialValues.unicorn.notifications.slack,
    };
  }

  const [busy, setBusy] = useState(false);
  const [isTestSuccess, setIsTestSuccess] = useState<boolean | undefined>(
    undefined,
  );

  const { connectSlack } = useConnectSlack();
  const campaignCRUDActions = useCampaignCRUDActions();
  const { getChannels, sendTest } = useSlackCRUDActions();

  const sendAsBotOptions = [
    { id: 1, name: 'Yes' },
    { id: 2, name: 'No' },
  ];
  const sendAsBot = sendAsBotOptions[0];
  const accountsOptions = accounts.map(account => {
    return {
      ...account,
      name: account.teamName,
    };
  });

  const formik = useFormik<SoldConfigurationsType>({
    initialValues: initials,
    validate: SOLDSlackHelper.validate(),
    onSubmit: async () => {},
  });

  const hasChanged = !isEqual(
    campaign?.soldConfigurations?.unicorn?.notifications?.slack,
    formik.values.unicorn.notifications?.slack,
  );

  const onSave = async (skipClose?: boolean) => {
    setBusy(true);
    try {
      if (campaignId) {
        await campaignCRUDActions.updateCampaign(
          {
            id: campaignId,
            soldConfigurations: formik.values,
          } as unknown as CampaignType,
          chatbotId,
        );

        successToast('Your changes have been saved.', 'top-left');

        if (!skipClose) {
          closeSlideout();
        }
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    try {
      if (hasChanged) {
        await onSave(true);
      }
      const res = await sendTest(formik.values.unicorn.notifications.slack);
      if (res.success) {
        setIsTestSuccess(true);
      }
    } catch (e) {
      setIsTestSuccess(false);
    }
  };

  const retrieveChannels = async teamId => {
    await getChannels(teamId);
  };

  const setField = (fieldName: string, value: any) => {
    formik.setFieldValue(`unicorn.notifications.slack.${fieldName}`, value);
  };
  const getField = (fieldName: string) => {
    return formik.values?.unicorn?.notifications?.slack?.[fieldName];
  };

  const onWorkspaseChange = (account: SlackAccount) => {
    retrieveChannels(account.teamId);
    setField('teamId', account.teamId);
    setField('teamName', account.teamName);
    setField('slackChannelName', '');
    setField('slackChannelId', '');
  };

  const onSendAsBotChange = (option: DropdownOption) => {
    setField('sendAsBot', option.id === sendAsBot.id ? true : false);
    if (option.id !== sendAsBot.id) {
      setField('botName', null);
      setField('assetUrl', null);
    }
  };

  const isSendAsBot = getField('sendAsBot');

  useEffect(() => {
    formik.validateForm();

    if (getField('teamId')) {
      retrieveChannels(getField('teamId'));
    }
  }, []);

  const onClose = () =>
    onBeforeClose(
      campaign?.soldConfigurations?.unicorn?.notifications?.slack,
      formik.values.unicorn.notifications?.slack,
    );

  const close = async () => {
    try {
      await onClose();
      closeSlideout();
      onCloseProp();
    } catch (err) {}
  };

  const {
    setRef,
    close: closeSlideout,
    onBeforeClose,
  } = useSlideoutOnClose(onClose, menuType);

  const isSaveDisabled =
    busy ||
    slackBusy ||
    !getField('teamId') ||
    !getField('slackChannelId') ||
    !getHTMLText(getField('content'));
  const { teamId, slackChannelId, content } =
    formik.errors as unknown as SlackFormFields;

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <BackButton onClick={close} />
        <Heading size={3}>Slack Notifications</Heading>
        <Tooltip
          css={{ content: css.tooltip }}
          anchorElement={
            <div className={css.buttonsRow}>
              <Button
                css={{ container: css.save }}
                onClick={test}
                disabled={isSaveDisabled}
              >
                Test Connection
              </Button>

              <Button
                css={{ container: css.save }}
                onClick={() => onSave(false)}
                disabled={isSaveDisabled}
              >
                Save
              </Button>
            </div>
          }
          toolTipDisabled={!isSaveDisabled}
          useHover
        >
          Workspace, channel and message are required.
        </Tooltip>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      {isTestSuccess !== undefined && (
        <div
          className={classNames(css.row, css.testBlock, {
            [css.failedTest]: !isTestSuccess,
          })}
        >
          <span
            className={css.closeTestBlock}
            onClick={() => setIsTestSuccess(undefined)}
          >
            <CloseOutlined />
          </span>
          <div className={css.column}>
            {isTestSuccess ? (
              <span className={css.testNoteBold}>
                {' '}
                <CheckCircleFilled className={css.checkCircule} /> Message sent
                successfully!{' '}
              </span>
            ) : (
              <span className={css.testNoteBold}>
                <WarningFilled className={css.warningIcon} />
                Test failed
              </span>
            )}
            <span className={css.testNote}>
              {' '}
              {isTestSuccess
                ? 'Check your Slack channel to view it'
                : 'Check your notification settings'}
            </span>
          </div>
        </div>
      )}
      <Menu.Content className={css.content}>
        <div className={css.row}>
          <div className={css.subtitle}>
            {' '}
            <SlackIcon /> Workspace{' '}
          </div>
          <div className={css.column}>
            <PrimarySelect
              className={css.select}
              activeItemClassName={css.activeItem}
              placeholder="Choose Workspace"
              options={accountsOptions}
              value={accountsOptions.find(
                acc => acc.teamId === getField('teamId'),
              )}
              onChange={account => onWorkspaseChange(account as SlackAccount)}
            />
            <div className={css.action} onClick={connectSlack}>
              <SlackOutlined color="#2f54eb" /> Connect Workspace
              {!getField('teamId') && <div className={css.error}>{teamId}</div>}
            </div>
          </div>
        </div>
        <Line className={css.line} />

        <div className={css.row}>
          <div className={css.subtitle}>
            {' '}
            <SlackIcon /> Channel{' '}
          </div>
          <div className={css.column}>
            <PrimarySelect
              className={css.select}
              activeItemClassName={css.activeItem}
              placeholder="Choose a Channel"
              options={slackChannels}
              value={slackChannels.find(
                channel => channel.name === getField('slackChannelName'),
              )}
              onChange={channel => {
                setField('slackChannelName', channel.name);
                setField('slackChannelId', channel.id);
              }}
              isLoading={slackBusy}
            />
            <div
              className={css.action}
              onClick={() => retrieveChannels(getField('teamId'))}
            >
              <SyncOutlined color="#2f54eb" /> Refresh list
              {!getField('slackChannelId') && (
                <div className={css.error}>{slackChannelId}</div>
              )}
            </div>
          </div>
        </div>
        <Line className={css.line} />

        <div className={css.column}>
          <div className={css.subtitle}>
            <SlackIcon />
            Message Text
          </div>

          <Editor
            styleMenubar={{ display: 'none' }}
            attributes={attributes}
            value={getField('content')}
            onChange={state => {
              setField('content', getHTMLStringFromState(state));
            }}
          />
          <div>
            {!getHTMLText(getField('content')) && (
              <div className={css.error}>{content || 'Message Required'}</div>
            )}
          </div>
        </div>
        <Line className={css.line} />

        <div className={css.row}>
          <div className={css.subtitle}>
            {' '}
            <SlackIcon /> Send as a bot?{' '}
          </div>
          <div className={css.column}>
            <PrimarySelect
              className={css.select}
              activeItemClassName={css.activeItem}
              options={sendAsBotOptions}
              value={getField('sendAsBot') ? sendAsBot : sendAsBotOptions[1]}
              onChange={option => onSendAsBotChange(option)}
            />
            <div className={css.note}>
              If you choose no, this message will appear from you.
            </div>
          </div>
        </div>
        <Line className={css.line} />

        <div className={css.row}>
          <div className={css.subtitle}>
            <SlackIcon /> Image for the bot
          </div>
          <div className={css.column}>
            <ImageUploader
              className={css.imageUploader}
              css={{ dropArea: css.imageUploaderDropArea }}
              needsFileModification
              file={getField('assetUrl') || ''}
              onUpload={(_, url) => setField('assetUrl', url)}
              onRemove={() => setField('assetUrl', null)}
              disabled={!isSendAsBot}
              fixedRatio
              withRemoveBtn
              newStyle
            />
            <div className={css.note}>
              Image should be square and a minimum of 512x512 pixels, but no
              larger than 1024x1024 pixels.
            </div>
          </div>
        </div>
        <Line className={css.line} />

        <div className={css.row}>
          <div className={css.subtitle}>
            {' '}
            <SlackIcon /> Bot Name{' '}
          </div>
          <div className={css.column}>
            <Input
              css={{
                container: classNames(css.input, {
                  [css.inputDisabled]: !isSendAsBot,
                }),
              }}
              value={getField('botName')}
              disabled={!isSendAsBot}
              onChange={e => setField('botName', e.target.value)}
              placeholder="MobileMonkey"
              type="text"
            />
            <div className={css.note}>
              Specify the bot name which appears as a bold username above the
              message inside Slack. Defaults to MobileMonkey.
            </div>
          </div>
        </div>
      </Menu.Content>
    </Menu>
  );
};
